// ./src/components/generic/EmbeddingLayoutTypeSelector/EmbeddingLayoutTypeSelector.tsx
import React from "react";
import { Select, SelectProps } from "@mantine/core";
import { AppIcon } from "../../generic/AppIcon";

const LAYOUT_TYPE_OPTIONS = [
  {
    value: "grid",
    label: "Grid",
    icon: "GridPattern",
    color: "blue",
    description: "Grid layout for multiple events",
  },
  {
    value: "calendar",
    label: "Calendar",
    icon: "Calendar",
    color: "green",
    description: "Calendar view layout",
  },
] as const;

export type EmbeddingLayoutType = "grid" | "calendar";

export interface EmbeddingLayoutTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current layout type value */
  value: string | null;

  /** Callback when layout type changes */
  onChange: (value: string | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const EmbeddingLayoutTypeSelector: React.FC<
  EmbeddingLayoutTypeSelectorProps
> = ({
  value,
  onChange,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select layout type",
  ...selectProps
}) => {
  // Prepare select data with optional descriptions
  const selectData = LAYOUT_TYPE_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // Get current option for icon display
  const currentOption = LAYOUT_TYPE_OPTIONS.find((opt) => opt.value === value);
  const leftSection =
    withIcons && currentOption ? (
      <AppIcon
        iconName={currentOption.icon as any}
        size={16}
        style={{
          color:
            currentOption.color === "blue"
              ? "var(--mantine-color-blue-6)"
              : "var(--mantine-color-green-6)",
        }}
      />
    ) : undefined;

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      leftSection={leftSection}
      clearable={false}
      size="md"
    />
  );
};

// Export layout type options for use in other components
export { LAYOUT_TYPE_OPTIONS };
